import React from 'react';
import { css } from '@emotion/react';
import { MediaHint, MediaRef } from '$/GraphQL/schema/schema';
import { WithUploadImage } from '$/components/WithUploadImage/WithUploadImage';
import { Avatar, Spinner } from '$/images/Icons';
import { useMe } from '$/hooks/useMe';
import { AddButton, FloatingRemoveButton } from '../Button/Button';
import { useImageSrcFromRef } from '$/hooks/useImageSrcFromRef';
import Text from '../Text';

export interface ProfilePictureUploadProps {
  value?: MediaRef;
  userName?: string;
  onChange?: (url: MediaRef | null | undefined) => void;
  onlyAvatar?: boolean;
  size?: { width: number; height: number };
}

const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  userName,
  onlyAvatar,
  size,
  ...props
}) => {
  const me = useMe();
  const refValue = props.value;
  const dim = React.useMemo(() => size ?? { width: 75, height: 75 }, [size]);

  const profileUrl = useImageSrcFromRef(refValue, {
    w: dim.width,
    h: dim.height,
    mask: 'ellipse',
    fit: 'facearea',
    ar: 1,
    faceindex: 1,
    facepad: 5,
  });

  return (
    <div
      className='flex justify-between md:w-full profile-picture-wrap'
      css={css`
        margin-bottom: 1rem;
      `}
    >
      <WithUploadImage
        avatar
        name='profile'
        value={refValue}
        onChange={props.onChange}
        asset={{
          entity: 'user',
          entityId: me.id,
          name: 'profile',
          container: {
            ...dim,
            hint: MediaHint.Profile,
          },
          fit: 'cover',
          position: 'center center',
        }}
      >
        {({ loading, onRemove }): JSX.Element => (
          <React.Fragment>
            <div className='relative'>
              {profileUrl ? <FloatingRemoveButton onClick={onRemove} /> : <AddButton />}
              <div
                className='image-show cursor-pointer hover:bg-gray-300 bg-gray-200 bg-cover border rounded-full overflow-hidden flex items-center justify-center'
                css={css`
                  width: ${dim.width}px;
                  height: ${dim.height}px;
                `}
              >
                {!profileUrl && !loading && <Avatar className='h-2e' />}

                {profileUrl && !loading && (
                  <img
                    src={profileUrl}
                    css={css`
                      width: ${dim.width}px;
                      height: ${dim.height}px;
                    `}
                  />
                )}
                {loading && (
                  <Spinner
                    className='text-color-iconGrey h-2/3'
                    css={css`
                      position: relative;
                      left: 1px;
                    `}
                  />
                )}
              </div>
            </div>
            {!onlyAvatar && (
              <div className='ml-5 flex flex-col justify-center'>
                <Text.subhead className='mb-1/8e text-gray-0 leading-normal'>
                  {userName || `${me.firstName} ${me.lastName}`}
                </Text.subhead>
                <Text.body className='text-teal-4 text-md leading-tight font-medium cursor-pointer'>
                  Upload new photo
                </Text.body>
              </div>
            )}
          </React.Fragment>
        )}
      </WithUploadImage>
    </div>
  );
};

export default ProfilePictureUpload;
